import _ from 'lodash';
import { IObservable, Subject } from '~/rage/utils';
import {
	HandlingMode,
	IClientConfigs,
	IHandlingTuningPreset,
	TechTuningCategory,
	TechTuningSection,
	VehicleHandling,
	handlingProperties,
	serializeHandling,
} from '~/shared';
import { getCurrentDateString } from '~/utils/date';
import { IVehicleService } from '../VehicleService';
import {
	IHandlingTuningService,
	TuningSectionConfig,
} from './handlingTuningServiceTypes';

export class HandlingTuningServiceStub implements IHandlingTuningService {
	private handling = new Subject<VehicleHandling>({});
	private presets = new Subject<IHandlingTuningPreset[]>([]);
	private sections = new Subject<TechTuningSection[]>([
		TechTuningSection.ENGINE,
		TechTuningSection.TRANSMISSION,
		TechTuningSection.BRAKES,
		TechTuningSection.TYRES,
	]);

	private allSections = new Subject<TuningSectionConfig[]>([
		{
			id: TechTuningSection.ENGINE,
			name: 'Двигатель',
			requiredCategory: TechTuningCategory.ENGINE,
		},
		{
			id: TechTuningSection.TRANSMISSION,
			name: 'Трансмиссия',
			requiredCategory: TechTuningCategory.ENGINE,
		},
		{
			id: TechTuningSection.BRAKES,
			name: 'Тормозная система',
			requiredCategory: TechTuningCategory.BRAKES,
		},
		{
			id: TechTuningSection.TYRES,
			name: 'Сцепление',
			requiredCategory: TechTuningCategory.TIRES,
		},
		{
			id: TechTuningSection.SUSPENSION,
			name: 'Подвеска',
			requiredCategory: TechTuningCategory.SUSPENSION,
		},
	]);

	private stubPreset: IHandlingTuningPreset = {
		name: 'Preset 1',
		info: 'Пресет для гонок от Димы',
		mode: HandlingMode.Comfort,
		handling: {},
		favorite: false,
		author: 'Jevico',
		creationDate: getCurrentDateString(),
		vehicleConfigId: 'test',
	};

	private handlingTuning = new Subject<
		IClientConfigs['vehicleTechTuning']['parameters']
	>([]);
	private handlingTuningStub: IClientConfigs['vehicleTechTuning']['parameters'] =
		[
			{
				property: 'fInitialDriveForce',
				step: 1,
				limits: [0, 25],
				name: 'Сила ускорения',
				section: TechTuningSection.ENGINE,
			},
			{
				property: 'fDriveInertia',
				step: 0.1,
				limits: [0, 2.5],
				name: 'Коэфф. набора оборотов',
				section: TechTuningSection.ENGINE,
			},
			{
				property: 'fBrakeForce',
				step: 1,
				limits: [0, 10],
				name: 'Сила торможения',
				section: TechTuningSection.BRAKES,
			},
			{
				property: 'fBrakeBiasFront',
				step: 0.1,
				limits: [0, 1],
				name: 'Баланс тормозного усилия',
				section: TechTuningSection.BRAKES,
			},
			{
				property: 'fHandBrakeForce',
				step: 0.1,
				limits: [0, 1],
				name: 'Сила ручного тормоза',
				section: TechTuningSection.BRAKES,
			},
			{
				property: 'fTractionBiasFront',
				step: 0.1,
				limits: [0, 1],
				name: 'Баланс сцепления',
				section: TechTuningSection.TYRES,
			},
			{
				property: 'fTractionCurveMax',
				step: 0.1,
				limits: [0, 3],
				name: 'Коэфф. сцепления в поворотах',
				section: TechTuningSection.TYRES,
			},
			{
				property: 'fTractionCurveMin',
				step: 0.1,
				limits: [0, 3],
				name: 'Коэфф. сцепления при разгоне',
				section: TechTuningSection.TYRES,
			},
			{
				property: 'fTractionCurveLateral',
				step: 1,
				limits: [10, 30],
				name: 'Коэфф. бокового сцепления',
				section: TechTuningSection.TYRES,
			},
			{
				property: 'fSuspensionForce',
				step: 0.1,
				limits: [0, 5],
				name: 'Сила подвески',
				section: TechTuningSection.SUSPENSION,
			},
			{
				property: 'fSuspensionRaise',
				step: 0.1,
				limits: [-1, 1],
				name: 'Высота подвески',
				section: TechTuningSection.SUSPENSION,
			},
			{
				property: 'fSuspensionBiasFront',
				step: 0.1,
				limits: [0, 1],
				name: 'Баланс подвески',
				section: TechTuningSection.SUSPENSION,
			},
			{
				property: 'fSteeringLock',
				step: 5,
				limits: [1, 90],
				name: 'Угол поворота колес',
				section: TechTuningSection.SUSPENSION,
			},
			{
				property: 'fDriveBiasFront',
				step: 0.1,
				limits: [0, 1],
				name: 'Привод',
				section: TechTuningSection.TRANSMISSION,
			},
		];

	private stockHandlingStub: VehicleHandling = {};

	constructor(private readonly vehicles: IVehicleService) {
		const handlingTuning = _.cloneDeep(this.handlingTuningStub);
		this.handlingTuning.next(handlingTuning);

		const presets: IHandlingTuningPreset[] = [];
		for (const mode of Object.values(HandlingMode)) {
			for (let i = 0; i < Math.ceil(Math.random() * 15); i++) {
				const handling = this.clampHandling(this.getRandomHandling());
				const favorite = Math.random() < 0.5;
				presets.push({
					...this.stubPreset,
					mode,
					handling,
					favorite,
				});
			}
		}

		this.presets.next(presets);

		this.stockHandlingStub = this.getRandomHandling();
		this.handling.next(this.stockHandlingStub);
	}

	private getRandomHandling(): VehicleHandling {
		const handling: VehicleHandling = {};
		const handlingTuning = this.handlingTuning.getValue();

		// @TODO property.limits - сток, нужны кастом пределы
		handlingProperties.forEach((property) => {
			const foundProperty = handlingTuning.find((e) => e.property === property);
			if (foundProperty) {
				handling[property] = _.random(
					foundProperty.limits[0] - foundProperty.limits[0] * 0.1,
					foundProperty.limits[1] + foundProperty.limits[1] * 0.1,
				);
			}
		});
		return handling;
	}

	public getParameters(): IObservable<
		IClientConfigs['vehicleTechTuning']['parameters']
	> {
		return this.handlingTuning;
	}

	public getSections(): IObservable<TechTuningSection[]> {
		return this.sections;
	}

	public getAllSections(): IObservable<TuningSectionConfig[]> {
		return this.allSections;
	}

	public getHandling(): IObservable<VehicleHandling> {
		return this.handling;
	}

	private clampHandling(handling: VehicleHandling): VehicleHandling {
		const vehicleHandling: VehicleHandling = _.cloneDeep(handling);
		const handlingTuning = this.handlingTuning.getValue();

		Object.entries(vehicleHandling).forEach(([property, value]) => {
			const foundProperty = handlingTuning.find((e) => e.property === property);
			if (value !== undefined && foundProperty) {
				if (typeof value === 'number') {
					// @TODO кастомные пределы
					vehicleHandling[property] = _.round(
						_.clamp(value, foundProperty.limits[0], foundProperty.limits[1]),
						3,
					);
				}
			}
		});

		return vehicleHandling;
	}

	private async getStockHandling(): Promise<VehicleHandling> {
		return this.clampHandling(this.stockHandlingStub);
	}

	public async fetchValues(): Promise<void> {
		const handling = this.clampHandling(this.getRandomHandling());
		this.handling.next(handling);

		console.log('fetchValues');
	}

	public setHandling(handling: VehicleHandling): void {
		this.handling.next(handling);
	}

	public async applyHandling(): Promise<void> {
		const handling = this.handling.getValue();
		console.log('techTuning:applyHandling', { handling });
	}

	public async setHandlingMode(mode: HandlingMode): Promise<void> {
		console.log('techTuning:setHandlingMode', { mode });
	}

	public async resetAllSectionHandling(): Promise<void> {
		const stockHandling = await this.getStockHandling();
		this.handling.next(stockHandling);

		console.log('resetAllSectionHandling');
	}

	public async resetSectionHandling(section: string): Promise<void> {
		const handling = this.handling.getValue();
		const stockHandling = await this.getStockHandling();

		const handlingTuning = this.handlingTuning.getValue();
		handlingTuning
			.filter((e) => e.section === section)
			.forEach((e) => {
				handling[e.property] = stockHandling[e.property];
			});

		this.handling.next(handling);

		console.log('resetSectionHandling', { section });
	}

	public async resetPropertyHandling(property: string): Promise<void> {
		const handling = { ...this.handling.getValue() };
		const stockHandling = await this.getStockHandling();
		handling[property] = stockHandling[property];

		this.handling.next(handling);

		console.log('resetPropertyHandling', { property });
	}

	public getPresets(): IObservable<IHandlingTuningPreset[]> {
		return this.presets;
	}

	public async addPreset(name: string, info: string = ''): Promise<void> {
		const mode = this.vehicles.getLocalVehicleHandlingMode().getValue();
		const handling = this.handling.getValue();
		const preset: IHandlingTuningPreset = {
			name,
			info,
			mode,
			handling: serializeHandling(handling),
			favorite: false,
			author: 'stdian',
			creationDate: getCurrentDateString(),
			vehicleConfigId: 'test',
		};

		const presets = _.cloneDeep(this.presets.getValue());
		presets.push(preset);
		this.presets.next(presets);

		console.log('addPreset', { name, info });
	}

	public applyPreset(presetIndex: number): void {
		const preset = this.presets.getValue()[presetIndex];
		if (preset !== undefined) {
			const { handling } = preset;
			this.setHandling(handling);
		}
		console.log('applyPreset', { presetIndex });
	}

	public async removePresetByIndex(presetIndex: number): Promise<void> {
		const presets = _.cloneDeep(this.presets.getValue());
		presets.splice(presetIndex, 1);
		this.presets.next(presets);
		console.log('removePresetByIndex', { presetIndex });
	}

	public async setPresetFavorite(
		presetIndex: number,
		favorite: boolean,
	): Promise<void> {
		const presets = _.cloneDeep(this.presets.getValue());
		if (presets[presetIndex] !== undefined) {
			presets[presetIndex].favorite = favorite;
			this.presets.next(presets);
		}
		console.log('setPresetFavorite', { presetIndex, favorite });
	}

	public async testDrive(): Promise<void> {
		console.log('techTuning:testDrive');
	}

	public async exitMenu(): Promise<void> {
		console.log('techTuning:exitMenu');
	}
}
